/**
 * Unit tables for the mechanics calculators.
 *
 * Every family converts through its SI base unit (metre, second, kilogram, newton,
 * joule, watt, radian). Factors are exact definitions or the SI/NIST values they are
 * quoted from (NIST SP 811, Appendix B.8):
 *
 * - inch 0.0254 m, foot 0.3048 m, yard 0.9144 m, mile 1609.344 m, nautical mile
 *   1852 m — all exact;
 * - standard gravity gₙ = 9.80665 m/s² — exact (CGPM 1901);
 * - avoirdupois pound 0.45359237 kg — exact, so the pound-force is
 *   0.45359237 × 9.80665 = 4.4482216152605 N exactly;
 * - dyne 10⁻⁵ N and thermochemical calorie 4.184 J — exact;
 * - kilowatt-hour 3.6 × 10⁶ J, electron volt 1.602176634 × 10⁻¹⁹ J — exact;
 * - International Table Btu 1055.05585262 J (NIST lists 1055.056);
 * - mechanical horsepower 550 ft·lbf/s = 745.69987158227… W, metric horsepower
 *   75 kgf·m/s = 735.49875 W, electrical horsepower 746 W exactly.
 *
 * Unit symbols are not translated, so they live here as data rather than in a widget's
 * strings.
 */

export interface PhysicsUnit {
  id: string;
  /** What the reader sees in the unit menu and in the working. */
  symbol: string;
  /** Size of one unit in the family's SI base unit. */
  factor: number;
  /** Used in the working but not offered in the unit menu (a plain fraction beside "%"). */
  hidden?: boolean;
}

export interface UnitFamily {
  /** SI base unit's symbol. */
  base: string;
  units: readonly PhysicsUnit[];
}

/** Standard acceleration of gravity, m/s² (exact). */
export const STANDARD_GRAVITY = 9.806_65;

const METRE_PER_INCH = 0.0254;
const METRE_PER_FOOT = 0.3048;
const KG_PER_POUND = 0.453_592_37;
const NEWTON_PER_POUND_FORCE = KG_PER_POUND * STANDARD_GRAVITY;
const JOULE_PER_FOOT_POUND_FORCE = METRE_PER_FOOT * NEWTON_PER_POUND_FORCE;
const SECONDS_PER_HOUR = 3600;
const NAUTICAL_MILE = 1852;

export const LENGTH: UnitFamily = {
  base: "m",
  units: [
    { id: "mm", symbol: "mm", factor: 1e-3 },
    { id: "cm", symbol: "cm", factor: 1e-2 },
    { id: "m", symbol: "m", factor: 1 },
    { id: "km", symbol: "km", factor: 1e3 },
    { id: "in", symbol: "in", factor: METRE_PER_INCH },
    { id: "ft", symbol: "ft", factor: METRE_PER_FOOT },
    { id: "yd", symbol: "yd", factor: 0.9144 },
    { id: "mi", symbol: "mi", factor: 1609.344 },
    { id: "nmi", symbol: "nmi", factor: NAUTICAL_MILE },
  ],
};

export const TIME: UnitFamily = {
  base: "s",
  units: [
    { id: "ms", symbol: "ms", factor: 1e-3 },
    { id: "s", symbol: "s", factor: 1 },
    { id: "min", symbol: "min", factor: 60 },
    { id: "h", symbol: "h", factor: SECONDS_PER_HOUR },
    { id: "d", symbol: "d", factor: 86_400 },
  ],
};

export const SPEED: UnitFamily = {
  base: "m/s",
  units: [
    { id: "mps", symbol: "m/s", factor: 1 },
    { id: "kph", symbol: "km/h", factor: 1000 / SECONDS_PER_HOUR },
    { id: "mph", symbol: "mph", factor: 1609.344 / SECONDS_PER_HOUR },
    { id: "fps", symbol: "ft/s", factor: METRE_PER_FOOT },
    { id: "kn", symbol: "kn", factor: NAUTICAL_MILE / SECONDS_PER_HOUR },
    { id: "cmps", symbol: "cm/s", factor: 1e-2 },
  ],
};

export const ACCELERATION: UnitFamily = {
  base: "m/s²",
  units: [
    { id: "mps2", symbol: "m/s²", factor: 1 },
    { id: "fps2", symbol: "ft/s²", factor: METRE_PER_FOOT },
    { id: "cmps2", symbol: "cm/s²", factor: 1e-2 },
    { id: "g", symbol: "g", factor: STANDARD_GRAVITY },
  ],
};

export const MASS: UnitFamily = {
  base: "kg",
  units: [
    { id: "mg", symbol: "mg", factor: 1e-6 },
    { id: "g", symbol: "g", factor: 1e-3 },
    { id: "kg", symbol: "kg", factor: 1 },
    { id: "t", symbol: "t", factor: 1000 },
    { id: "oz", symbol: "oz", factor: KG_PER_POUND / 16 },
    { id: "lb", symbol: "lb", factor: KG_PER_POUND },
  ],
};

export const FORCE: UnitFamily = {
  base: "N",
  units: [
    { id: "mN", symbol: "mN", factor: 1e-3 },
    { id: "N", symbol: "N", factor: 1 },
    { id: "kN", symbol: "kN", factor: 1e3 },
    { id: "lbf", symbol: "lbf", factor: NEWTON_PER_POUND_FORCE },
    { id: "kgf", symbol: "kgf", factor: STANDARD_GRAVITY },
    { id: "dyn", symbol: "dyn", factor: 1e-5 },
  ],
};

export const ENERGY: UnitFamily = {
  base: "J",
  units: [
    { id: "mJ", symbol: "mJ", factor: 1e-3 },
    { id: "J", symbol: "J", factor: 1 },
    { id: "kJ", symbol: "kJ", factor: 1e3 },
    { id: "MJ", symbol: "MJ", factor: 1e6 },
    { id: "cal", symbol: "cal", factor: 4.184 },
    { id: "kcal", symbol: "kcal", factor: 4184 },
    { id: "Wh", symbol: "Wh", factor: SECONDS_PER_HOUR },
    { id: "kWh", symbol: "kWh", factor: 3.6e6 },
    { id: "ftlbf", symbol: "ft·lbf", factor: JOULE_PER_FOOT_POUND_FORCE },
    { id: "Btu", symbol: "Btu", factor: 1055.055_852_62 },
    { id: "eV", symbol: "eV", factor: 1.602_176_634e-19 },
  ],
};

export const POWER: UnitFamily = {
  base: "W",
  units: [
    { id: "mW", symbol: "mW", factor: 1e-3 },
    { id: "W", symbol: "W", factor: 1 },
    { id: "kW", symbol: "kW", factor: 1e3 },
    { id: "MW", symbol: "MW", factor: 1e6 },
    { id: "hp", symbol: "hp", factor: 550 * JOULE_PER_FOOT_POUND_FORCE },
    { id: "PS", symbol: "PS", factor: 75 * STANDARD_GRAVITY },
    { id: "hpE", symbol: "hp (electric)", factor: 746 },
  ],
};

export const ANGLE: UnitFamily = {
  base: "rad",
  units: [
    { id: "deg", symbol: "°", factor: Math.PI / 180 },
    { id: "rad", symbol: "rad", factor: 1 },
  ],
};

export const ANGULAR_SPEED: UnitFamily = {
  base: "rad/s",
  units: [
    { id: "radps", symbol: "rad/s", factor: 1 },
    { id: "rpm", symbol: "rpm", factor: (2 * Math.PI) / 60 },
    { id: "rps", symbol: "rev/s", factor: 2 * Math.PI },
    { id: "degps", symbol: "°/s", factor: Math.PI / 180 },
  ],
};

export const MOMENTUM: UnitFamily = {
  base: "kg·m/s",
  units: [
    { id: "kgmps", symbol: "kg·m/s", factor: 1 },
    { id: "gcmps", symbol: "g·cm/s", factor: 1e-5 },
    { id: "lbfps", symbol: "lb·ft/s", factor: KG_PER_POUND * METRE_PER_FOOT },
  ],
};

export const IMPULSE: UnitFamily = {
  base: "N·s",
  units: [
    { id: "Ns", symbol: "N·s", factor: 1 },
    { id: "kNs", symbol: "kN·s", factor: 1e3 },
    { id: "lbfs", symbol: "lbf·s", factor: NEWTON_PER_POUND_FORCE },
  ],
};

export const TORQUE: UnitFamily = {
  base: "N·m",
  units: [
    { id: "Ncm", symbol: "N·cm", factor: 1e-2 },
    { id: "Nm", symbol: "N·m", factor: 1 },
    { id: "kNm", symbol: "kN·m", factor: 1e3 },
    { id: "lbfft", symbol: "lbf·ft", factor: JOULE_PER_FOOT_POUND_FORCE },
    {
      id: "lbfin",
      symbol: "lbf·in",
      factor: METRE_PER_INCH * NEWTON_PER_POUND_FORCE,
    },
  ],
};

export const SPRING_CONSTANT: UnitFamily = {
  base: "N/m",
  units: [
    { id: "Npm", symbol: "N/m", factor: 1 },
    { id: "Npcm", symbol: "N/cm", factor: 100 },
    { id: "kNpm", symbol: "kN/m", factor: 1000 },
    {
      id: "lbfpin",
      symbol: "lbf/in",
      factor: NEWTON_PER_POUND_FORCE / METRE_PER_INCH,
    },
  ],
};

export const MOMENT_OF_INERTIA: UnitFamily = {
  base: "kg·m²",
  units: [
    { id: "kgm2", symbol: "kg·m²", factor: 1 },
    { id: "gcm2", symbol: "g·cm²", factor: 1e-7 },
    {
      id: "lbft2",
      symbol: "lb·ft²",
      factor: KG_PER_POUND * METRE_PER_FOOT ** 2,
    },
  ],
};

export const ANGULAR_ACCELERATION: UnitFamily = {
  base: "rad/s²",
  units: [
    { id: "radps2", symbol: "rad/s²", factor: 1 },
    { id: "degps2", symbol: "°/s²", factor: Math.PI / 180 },
    { id: "rps2", symbol: "rev/s²", factor: 2 * Math.PI },
  ],
};

export const FREQUENCY: UnitFamily = {
  base: "Hz",
  units: [
    { id: "Hz", symbol: "Hz", factor: 1 },
    { id: "kHz", symbol: "kHz", factor: 1e3 },
    { id: "rpm", symbol: "rpm", factor: 1 / 60 },
  ],
};

/** A pure number — a coefficient of friction. One unit with no symbol, so no menu is drawn. */
export const COEFFICIENT: UnitFamily = {
  base: "",
  units: [{ id: "1", symbol: "", factor: 1 }],
};

/** Efficiency as a fraction (the base) or a percentage. */
export const EFFICIENCY: UnitFamily = {
  base: "",
  units: [
    { id: "pct", symbol: "%", factor: 0.01 },
    { id: "frac", symbol: "", factor: 1, hidden: true },
  ],
};

export function findUnit(family: UnitFamily, id: string): PhysicsUnit {
  return family.units.find((unit) => unit.id === id) ?? family.units[0];
}

/** A value in `unit` → the family's SI base unit. */
export function toBase(value: number, family: UnitFamily, id: string): number {
  return value * findUnit(family, id).factor;
}

/** A value in the SI base unit → `unit`. */
export function fromBase(
  value: number,
  family: UnitFamily,
  id: string
): number {
  return value / findUnit(family, id).factor;
}

/** Value in `from` units → value in `to` units. */
export function convert(
  value: number,
  family: UnitFamily,
  from: string,
  to: string
): number {
  return (value * findUnit(family, from).factor) / findUnit(family, to).factor;
}

/** Select options (value = id, label = symbol) for a family. */
export function unitOptions(
  family: UnitFamily
): { value: string; label: string }[] {
  return family.units
    .filter((unit) => unit.hidden !== true)
    .map((unit) => ({ value: unit.id, label: unit.symbol }));
}

/** The symbol of a unit id, for the working. */
export function unitSymbol(family: UnitFamily, id: string): string {
  return findUnit(family, id).symbol;
}

/** True when the unit is the family's base unit, so no conversion step is needed. */
export function isBaseUnit(family: UnitFamily, id: string): boolean {
  return findUnit(family, id).factor === 1;
}
